import { useCallback, useEffect, useState } from "react";
import { Download, LogOut } from "lucide-react";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import {
  apiClient,
  ErreurApi,
  libelleCategorie,
  libelleStatut,
  tailleLisible,
  type LotClient,
  type DocumentClient,
} from "@/client/api";
import { useSessionClient } from "@/client/session";

/**
 * Ce que le client voit — et l'essentiel est ce qu'il ne voit PAS.
 *
 * Ni montant, ni marge, ni commission, ni commentaire interne, ni imputation de
 * responsabilité : le §12.2 les interdit, et ils ne figurent tout simplement pas
 * dans ce que l'API de l'espace client renvoie. Il n'y a donc rien à filtrer ici,
 * et rien qu'un oubli de filtrage puisse laisser passer.
 */
export function PageTableauClient() {
  const session = useSessionClient();
  const { deconnexion } = session;
  const [lots, setLots] = useState<LotClient[] | null>(null);
  const [documents, setDocuments] = useState<DocumentClient[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback((signal?: AbortSignal) => {
    setErreur(null);
    Promise.all([apiClient.lots(signal), apiClient.documents(signal)])
      .then(([c, d]) => {
        setLots(c);
        setDocuments(d);
      })
      .catch((cause) => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setErreur(cause instanceof ErreurApi ? cause.message : "Une erreur est survenue.");
      });
  }, []);

  useEffect(() => {
    const controleur = new AbortController();
    charger(controleur.signal);
    return () => controleur.abort();
  }, [charger]);

  const courriel = session.statut === "connecte" ? session.identite.email : "";

  return (
    <div className="min-h-full">
      <header className="border-b border-filet bg-white">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6">
          <div>
            <p className="text-lg font-semibold tracking-[0.18em] text-anthracite">LABGEST</p>
            <p className="text-xs text-encre-douce">Votre espace · {courriel}</p>
          </div>
          <button
            type="button"
            onClick={() => void deconnexion()}
            className="inline-flex items-center gap-2 rounded-[6px] px-3 py-2 text-sm text-encre-douce hover:bg-filet/60 hover:text-encre"
          >
            <LogOut className="h-4 w-4" aria-hidden />
            Se déconnecter
          </button>
        </div>
      </header>

      <main className="mx-auto flex max-w-4xl flex-col gap-8 px-4 py-6 sm:px-6 sm:py-8">
        {erreur ? <Erreur message={erreur} onReessayer={() => charger()} /> : null}

        <section className="flex flex-col gap-3">
          <h1 className="text-xl font-semibold text-encre">Vos lots</h1>
          {!erreur && lots === null ? <Chargement /> : null}
          {lots !== null && lots.length === 0 ? (
            <Carte>
              <Vide titre="Aucun lot pour le moment" />
            </Carte>
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2">
            {(lots ?? []).map((lot) => (
              <Carte key={lot.id}>
                <CorpsCarte className="flex flex-col gap-1">
                  <p className="font-mono text-xs text-encre-douce">{lot.code_lot}</p>
                  <p className="text-lg font-medium text-encre">{lot.intitule}</p>
                  {lot.ville ? <p className="text-sm text-encre-douce">{lot.ville}</p> : null}
                  <p className="mt-2">
                    <span className="inline-block rounded-[6px] bg-chene-clair px-2 py-1 text-xs text-encre">
                      {libelleStatut(lot.statut_affiche)}
                    </span>
                  </p>
                </CorpsCarte>
              </Carte>
            ))}
          </div>
        </section>

        <section className="flex flex-col gap-3">
          <h2 className="text-xl font-semibold text-encre">Vos documents</h2>
          {!erreur && documents === null ? <Chargement /> : null}
          {documents !== null && documents.length === 0 ? (
            <Carte>
              <Vide
                titre="Aucun document pour le moment"
                detail="Les documents vous sont transmis au fur et à mesure de l'avancement."
              />
            </Carte>
          ) : null}
          {documents !== null && documents.length > 0 ? (
            <Carte>
              <EnTeteCarte className="text-sm text-encre-douce">
                {documents.length} document{documents.length > 1 ? "s" : ""} à votre disposition
              </EnTeteCarte>
              <ul>
                {documents.map((document) => (
                  <li
                    key={document.id}
                    className="flex flex-wrap items-center justify-between gap-3 border-b border-filet px-4 py-3 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-encre">
                        {libelleCategorie(document.categorie)}
                      </p>
                      <p className="truncate text-sm text-encre-douce">
                        {document.nom_fichier_origine} · {tailleLisible(document.taille_octets)}
                      </p>
                    </div>
                    <a
                      href={apiClient.lienDocument(document.id)}
                      className="inline-flex shrink-0 items-center gap-2 rounded-[6px] border border-filet px-3 py-2 text-sm text-encre hover:bg-chene-clair/40"
                    >
                      <Download className="h-4 w-4" aria-hidden />
                      Télécharger
                    </a>
                  </li>
                ))}
              </ul>
            </Carte>
          ) : null}
        </section>

        <p className="text-xs text-encre-douce">
          Une question sur vos travaux ? Contactez votre interlocuteur chez
          Les Ateliers Bordelais.
        </p>
      </main>
    </div>
  );
}
